import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { CodeEvent, CodeItem, CodeToolItem } from './types';
import { api } from './api';
import { wsBus } from './wsBus';

const MAX_ITEMS = 200;

type Action =
  | { t: 'event'; e: CodeEvent }
  | { t: 'user'; text: string }
  | { t: 'popUser' }
  | { t: 'clear' };

function trim(items: CodeItem[]): CodeItem[] {
  if (items.length <= MAX_ITEMS) return items;
  const dropped = items.length - MAX_ITEMS;
  return [{ role: 'note', text: `…省略了较早的 ${dropped} 条输出` }, ...items.slice(dropped)];
}

function reduce(items: CodeItem[], a: Action): CodeItem[] {
  switch (a.t) {
    case 'clear':
      return [];
    case 'user':
      return trim([...items, { role: 'user', text: a.text }]);
    case 'popUser': {
      for (let i = items.length - 1; i >= 0; i--) {
        if (items[i].role === 'user') {
          const c = items.slice();
          c.splice(i, 1);
          return c;
        }
      }
      return items;
    }
    case 'event': {
      const e = a.e;
      switch (e.kind) {
        case 'text': {
          if (!e.delta) return items;
          const last = items[items.length - 1];
          if (last && last.role === 'assistant') {
            const c = items.slice();
            c[c.length - 1] = { role: 'assistant', text: last.text + e.delta };
            return c;
          }
          return trim([...items, { role: 'assistant', text: e.delta }]);
        }
        case 'tool': {
          const tool: CodeToolItem = {
            role: 'tool',
            id: e.id ?? '',
            name: e.name ?? '',
            input: e.input ?? '',
          };
          return trim([...items, tool]);
        }
        case 'tool_result': {
          const c = items.slice();
          for (let i = c.length - 1; i >= 0; i--) {
            const it = c[i];
            if (it.role === 'tool' && it.id === e.toolUseId) {
              c[i] = { ...it, result: e.content ?? '', isError: e.isError };
              return c;
            }
          }
          return items; // no matching tool — ignore
        }
        case 'result':
          return e.isError && e.text ? trim([...items, { role: 'error', text: e.text }]) : items;
        default:
          return items; // status / session — no transcript change
      }
    }
    default:
      return items;
  }
}

export interface Coder {
  transcript: CodeItem[];
  working: boolean;
  sessionId: string | null;
  available: boolean;
  engine: 'claude' | 'codex';
  note: string;
  clearNote: () => void;
  ensureSubscribed: () => void;
  send: (text: string) => Promise<{ ok: boolean; restore?: string }>;
  stop: () => void;
  reset: () => void;
}

export function useCoder(): Coder {
  const [transcript, dispatch] = useReducer(reduce, []);
  const [working, setWorking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [available, setAvailable] = useState(false);
  const [engine, setEngine] = useState<'claude' | 'codex'>('claude');
  const [note, setNote] = useState('');
  const subscribed = useRef(false);

  useEffect(() => {
    api
      .capabilities()
      .then((c) => {
        setAvailable(!!c.codeMode?.available);
        setEngine(c.codeMode?.engine === 'codex' ? 'codex' : 'claude');
      })
      .catch(() => {});
    const off = wsBus.subscribe((m: any) => {
      if (m?.type !== 'code') return;
      const e = m as CodeEvent;
      if (e.kind === 'status') setWorking(!!e.working);
      else if (e.kind === 'session') setSessionId(e.sessionId ?? null);
      dispatch({ t: 'event', e });
    });
    return off;
  }, []);

  useEffect(() => {
    if (!note) return;
    const t = setTimeout(() => setNote(''), 5000);
    return () => clearTimeout(t);
  }, [note]);

  const ensureSubscribed = useCallback(() => {
    if (subscribed.current) return;
    subscribed.current = true;
    wsBus.joinChannel('code');
  }, []);

  const send = useCallback(
    async (text: string): Promise<{ ok: boolean; restore?: string }> => {
      ensureSubscribed();
      setNote('');
      dispatch({ t: 'user', text });
      setWorking(true); // optimistic — indicator shows before the first server event
      const r = await api.code(text);
      if (!r.ok) {
        dispatch({ t: 'popUser' });
        if (r.status !== 409) setWorking(false); // 409 = a run really is in progress; keep it
        setNote(
          r.status === 409
            ? '正在运行中…'
            : r.status === 413
              ? '内容过长'
              : r.status === 401 || r.status === 403
                ? '未授权'
                : '发送失败',
        );
        return { ok: false, restore: text };
      }
      return { ok: true };
    },
    [ensureSubscribed],
  );

  const stop = useCallback(() => {
    void api.codeStop();
  }, []);

  const reset = useCallback(() => {
    dispatch({ t: 'clear' });
    setSessionId(null);
    setWorking(false);
    setNote('');
    void api.codeReset();
  }, []);

  const clearNote = useCallback(() => setNote(''), []);

  return {
    transcript,
    working,
    sessionId,
    available,
    engine,
    note,
    clearNote,
    ensureSubscribed,
    send,
    stop,
    reset,
  };
}
